import { useNavigate } from 'react-router-dom';
import type { CapacityUtilization, RangeKey } from '@busy/api';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useT } from '../../lib/i18n';

// Tappable explainer for any Earnings Outlook stat. The barber taps a
// value on the Capacity Tile, this sheet opens with:
//
//   1. WHAT — one-sentence plain-English definition
//   2. FORMULA — symbolic recipe (with a "where … = …" line when nested)
//   3. YOUR NUMBERS — the formula with their actual values plugged in
//   4. TIP — one practical coaching move to grow the number
//
// One sheet, one stat. Replaces the older behavior where the earning
// rate tile linked straight to the Cut Log; the cut-log path lives on
// as a secondary button at the bottom of the earning-rate explainer
// so the "show me the underlying data" affordance isn't lost.

export type OutlookExplainerKind =
  | 'utilization'
  | 'earning-rate'
  | 'period-earned'
  | 'period-potential'
  | 'period-gap'
  | 'annual-projected'
  | 'annual-potential'
  | 'annual-gap';

export function OutlookExplainerSheet({
  kind,
  cap,
  range,
  onClose,
}: {
  kind: OutlookExplainerKind;
  cap: CapacityUtilization;
  range: RangeKey;
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();

  const titleKey = `dash.capacity.explainer.${kind}.title`;
  const whatKey = `dash.capacity.explainer.${kind}.what`;
  const tipKey = `dash.capacity.explainer.${kind}.tip`;

  const formula = buildFormula(kind, cap, range, t);

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t(titleKey)}>
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 md:p-6 space-y-4">
        <header className="pr-10">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('dash.capacity.explainer.eyebrow')}
          </div>
          <h2 className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight mt-0.5">
            {t(titleKey)}
          </h2>
        </header>

        {/* 1. What this is */}
        <p className="text-[14px] text-ink-700 leading-relaxed">{t(whatKey)}</p>

        {/* 2. Formula — symbolic */}
        <Section label={t('dash.capacity.explainer.formula')}>
          <div className="rounded-lg bg-bg-alt px-3 py-2.5 font-mono text-[13px] text-ink-900 leading-relaxed">
            {formula.symbolic}
            {formula.symbolicSub && (
              <div className="text-[12px] text-ink-500 mt-1.5 italic">
                {formula.symbolicSub}
              </div>
            )}
          </div>
        </Section>

        {/* 3. Your numbers — plugged in */}
        <Section label={t('dash.capacity.explainer.yourNumbers')}>
          <div className="rounded-lg bg-bg-alt px-3 py-2.5 font-mono text-[13px] text-navy-900 dark:text-gold-400 leading-relaxed tabular-nums">
            {formula.plugged ?? (
              <span className="text-ink-500 italic font-sans">
                {t('dash.capacity.explainer.notEnoughData')}
              </span>
            )}
          </div>
        </Section>

        {/* 4. Tip — coaching move */}
        <Section label={t('dash.capacity.explainer.tip')}>
          <div className="rounded-lg border border-gold-500/30 bg-gold-500/8 px-3 py-2.5 text-[13px] text-ink-900 leading-relaxed flex items-start gap-2">
            <LightbulbIcon className="w-3.5 h-3.5 text-gold-500 mt-0.5 shrink-0" />
            <span>{t(tipKey)}</span>
          </div>
        </Section>

        {/* 5. Secondary action — only on the earning-rate sheet, to
            preserve the old Cut Log link. Keeps "show me the cuts
            behind this rate" within one tap. */}
        {kind === 'earning-rate' && (
          <button
            type="button"
            onClick={() => {
              navigate(`/cuts?range=${range}`);
              onClose();
            }}
            className="w-full h-10 rounded-lg border border-line text-ink-700 font-semibold text-[13px] inline-flex items-center justify-center gap-2 hover:bg-bg-alt transition-colors"
          >
            {t('dash.capacity.explainer.seeCuts')}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </ModalShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Formula builders — pure transformations from a snapshot + kind into the
// symbolic recipe and the "your numbers" string. Anything null in the
// snapshot returns plugged=null so the sheet renders a "not enough data
// yet" italic line instead of "NaN/hr" or "$0".
// ────────────────────────────────────────────────────────────────────────────

type FormulaParts = {
  symbolic: string;
  /** Optional second line — used for nested definitions like "where target
   *  hours = weekly × range weeks × hourly target %". */
  symbolicSub?: string;
  /** Numbers plugged in. Null = not enough data yet. */
  plugged: string | null;
};

function buildFormula(
  kind: OutlookExplainerKind,
  cap: CapacityUtilization,
  range: RangeKey,
  t: (k: string, vars?: Record<string, string | number>) => string,
): FormulaParts {
  const rangeWeeks = rangeToWeeks(range);
  const rangeLabel = t(`range.compact.${range}`);
  const weeklyTarget = cap.target_hours_per_week;
  const days = cap.target_days_per_week ?? 5;
  const pct = cap.hourly_target_pct;
  const periodRevDollars = Math.round(cap.period_revenue_cents / 100);
  // Rateable subset (cuts ≥ 6 min only) — drives the earning-rate math
  // display so the numerator and denominator shown match the $/hr that
  // the user sees on the tile. Without these the math line would
  // include sub-6-min cuts and not equal the displayed rate.
  const periodHrsRateable = cap.period_actual_hours_rateable;
  const periodRevRateableDollars = Math.round(
    cap.period_revenue_rateable_cents / 100,
  );
  const rateDollars =
    cap.period_dollar_per_hour_cents != null
      ? Math.round(cap.period_dollar_per_hour_cents / 100)
      : null;
  const periodTargetHrs = cap.period_target_hours;
  const periodPotentialDollars =
    cap.period_potential_cents != null
      ? Math.round(cap.period_potential_cents / 100)
      : null;
  const periodGapDollars =
    cap.period_gap_cents != null ? Math.round(cap.period_gap_cents / 100) : null;
  const projectedDollars = Math.round(cap.projected_annual_cents / 100);
  const potentialAnnualDollars =
    cap.potential_annual_cents != null
      ? Math.round(cap.potential_annual_cents / 100)
      : null;
  const annualGapDollars =
    cap.annual_gap_cents != null ? Math.round(cap.annual_gap_cents / 100) : null;
  const annualTargetHrs = cap.annual_target_hours;
  const weeksElapsed = cap.ytd_weeks_elapsed;
  // % utilized inputs — this week's actual hours vs this week's target.
  // Calendar-week scoped (Mon–Sun), not range-scoped, since the target
  // itself is per-calendar-week.
  const thisWeekHrs = cap.this_week_actual_hours;
  const thisWeekTarget = cap.this_week_target_hours ?? cap.target_hours_per_week ?? 0;
  const utilizationPct =
    thisWeekHrs > 0 && thisWeekTarget > 0
      ? Math.min(100, Math.round((thisWeekHrs / thisWeekTarget) * 100))
      : 0;

  switch (kind) {
    case 'utilization':
      return {
        symbolic: 'this week\'s hours ÷ this week\'s target × 100%',
        symbolicSub: '(capped at 100% — going past target doesn\'t bump it higher)',
        plugged:
          thisWeekTarget > 0
            ? `${fmt1(thisWeekHrs)}h ÷ ${thisWeekTarget}h × 100% = ${utilizationPct}%`
            : null,
      };

    case 'earning-rate':
      return {
        symbolic: 'total revenue ÷ total hours',
        symbolicSub: '(cuts ≥ 6 min only)',
        plugged:
          rateDollars != null && periodHrsRateable > 0
            ? `$${periodRevRateableDollars.toLocaleString()} ÷ ${fmt1(periodHrsRateable)}h = $${rateDollars}/hr · ${rangeLabel}`
            : null,
      };

    case 'period-earned':
      return {
        symbolic: 'sum(price) for all cuts in the period',
        plugged: `$${periodRevDollars.toLocaleString()} · ${rangeLabel}`,
      };

    case 'period-potential': {
      // Range='today' uses the daily target form (weekly / days × pct)
      // because the barber explicitly chose today as a workday. Every
      // other range uses the weekly-spread form (weekly × rangeWeeks ×
      // pct). Mirrors the hooks.ts math for period_target_hours.
      const isTodayRange = range === 'today';
      return {
        symbolic: isTodayRange
          ? 'earning rate × daily target hours'
          : 'earning rate × target hours for the period',
        symbolicSub: isTodayRange
          ? 'where daily target = weekly hours ÷ days per week × hourly target %'
          : 'where target hours = weekly hours × range weeks × hourly target %',
        plugged: (() => {
          if (
            periodPotentialDollars == null ||
            rateDollars == null ||
            weeklyTarget == null ||
            periodTargetHrs == null
          ) return null;
          if (isTodayRange) {
            return `$${rateDollars}/hr × (${weeklyTarget}h ÷ ${days} days × ${pct}%) = $${periodPotentialDollars.toLocaleString()}`;
          }
          return `$${rateDollars}/hr × (${weeklyTarget}h × ${fmt2(rangeWeeks)} × ${pct}%) = $${periodPotentialDollars.toLocaleString()}`;
        })(),
      };
    }

    case 'period-gap':
      return {
        symbolic: 'period potential − period earned',
        plugged:
          periodGapDollars != null && periodPotentialDollars != null
            ? `$${periodPotentialDollars.toLocaleString()} − $${periodRevDollars.toLocaleString()} = $${periodGapDollars.toLocaleString()}`
            : null,
      };

    case 'annual-projected': {
      // Days in the selected range — mirrors hooks.ts. YTD is dynamic
      // (Jan 1 → today); the others are fixed.
      const rangeDays =
        range === 'today' ? 1
        : range === '7d'   ? 7
        : range === '14d'  ? 14
        : range === '30d'  ? 30
        : range === '90d'  ? 90
        : Math.max(1, Math.round(weeksElapsed * 7));
      return {
        symbolic: 'revenue in range ÷ days in range × 365',
        symbolicSub: '(matches the range you have selected — pick a wider range for a steadier projection)',
        plugged:
          rangeDays > 0
            ? `$${periodRevDollars.toLocaleString()} ÷ ${rangeDays} days × 365 = $${projectedDollars.toLocaleString()} · ${rangeLabel}`
            : null,
      };
    }

    case 'annual-potential':
      return {
        symbolic: 'earning rate × annual target hours',
        symbolicSub: 'where annual target hours = weekly hours × 52 × hourly target %',
        plugged:
          potentialAnnualDollars != null && rateDollars != null && weeklyTarget != null && annualTargetHrs != null
            ? `$${rateDollars}/hr × (${weeklyTarget}h × 52 × ${pct}%) = $${potentialAnnualDollars.toLocaleString()}`
            : null,
      };

    case 'annual-gap':
      return {
        symbolic: 'year-end potential − year-end projected',
        plugged:
          annualGapDollars != null && potentialAnnualDollars != null
            ? `$${potentialAnnualDollars.toLocaleString()} − $${projectedDollars.toLocaleString()} = $${annualGapDollars.toLocaleString()}`
            : null,
      };
  }
  // Silence days var when not used in a branch — avoids dead-code warnings.
  void days;
}

function rangeToWeeks(range: RangeKey): number {
  switch (range) {
    case 'today': return 1 / 7;
    case '7d':    return 1;
    case '14d':   return 2;
    case '30d':   return 30 / 7;
    case '90d':   return 90 / 7;
    case 'ytd': {
      const now = new Date();
      const jan1 = new Date(now.getFullYear(), 0, 1);
      const ms = now.getTime() - jan1.getTime();
      return Math.max(1 / 7, ms / (7 * 24 * 60 * 60 * 1000));
    }
    case 'custom':
      // Custom ranges don't expose their bounds to this helper (it
      // only sees the RangeKey enum). Default to 1 week so the Outlook
      // explainer's pace math stays sensible — the Dashboard suppresses
      // vs-prior comparisons on 'custom' anyway, so the precise value
      // here doesn't drive a user-facing decision.
      return 1;
  }
}

function fmt1(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}

function fmt2(n: number): string {
  if (n >= 10) return n.toFixed(0);
  if (n >= 1) return n.toFixed(1).replace(/\.0$/, '');
  return n.toFixed(2);
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="text-[10px] tracking-[0.14em] uppercase font-extrabold text-ink-500 mb-1.5">
        {label}
      </div>
      {children}
    </section>
  );
}

function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2z" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
